// TelcoTrack — neutrální zdroj záfuku (ROZVADEC nebo SPOJKOVISTE).
//
// Historicky byl zdroj záfuku vždy rozvaděč (`*Rozvadec*` / `zafuk_z_rozvadec_*`).
// Zdrojem může být rozvaděč i libovolné spojkoviště ve stejném polygonu (kromě cíle).
// Kanonický tvar je `ZafukSourceRef`; staré klíče se čtou jako typ ROZVADEC.
//
// Fail-closed kompatibilita: legacy `*Rozvadec*` klíče se zapisují **jen** pro typ
// ROZVADEC. Pro SPOJKOVISTE zůstávají prázdné, aby starší klienti a dotazy nad
// `pripojovany_zafouknuto_z_rozvadec_object_id` (svary, finance, linked budovy)
// nezpracovaly spojkoviště omylem jako rozvaděč.

export const ZAFUK_SOURCE_OBJECT_TYPES = ['ROZVADEC', 'SPOJKOVISTE'] as const;
export type ZafukSourceObjectType = (typeof ZAFUK_SOURCE_OBJECT_TYPES)[number];

export const ZAFUK_SOURCE_TYPE_LABELS: Record<ZafukSourceObjectType, string> = {
  ROZVADEC: 'Rozvaděč',
  SPOJKOVISTE: 'Spojkoviště',
};

/** Neutrální odkaz na zdroj záfuku. `name` je uložená kopie popisku pro zobrazení. */
export interface ZafukSourceRef {
  objectId: string;
  objectType: ZafukSourceObjectType;
  name: string | null;
}

/** Ploché klíče pro DTO / DB řádky / metadata (bez vnořeného objektu). */
export interface ZafukSourceFlatFields {
  zafukSourceObjectId: string | null;
  zafukSourceObjectType: ZafukSourceObjectType | null;
  zafukSourceName: string | null;
}

/** Legacy ploché klíče — zachovány pro čtení starých dat a starší klienty. */
export interface ZafukSourceLegacyFields {
  zafukZRozvadecObjectId: string | null;
  zafukZRozvadecName: string | null;
}

// ---------------------------------------------------------------------------
// UI texty
// ---------------------------------------------------------------------------

export const ZAFUK_SOURCE_PICK_FROM_MAP_LABEL = 'Vybrat z mapy';
export const ZAFUK_SOURCE_PICK_FROM_LIST_LABEL = 'Vybrat rozvaděč ze seznamu';
export const ZAFUK_SOURCE_CLOSE_LIST_LABEL = 'Zavřít seznam';
export const ZAFUK_SOURCE_SEARCH_PLACEHOLDER = 'Hledat rozvaděč…';
export const ZAFUK_SOURCE_PICK_CONFIRM_LABEL = 'Potvrdit';
export const ZAFUK_SOURCE_PICK_CANCEL_LABEL = 'Zrušit';
export const ZAFUK_SOURCE_PICK_HINT =
  'Klikněte na mapě na rozvaděč nebo spojkoviště, ze kterého záfuk vede.';
export const ZAFUK_SOURCE_LABEL = 'Zafouknuto z';
export const ZAFUK_SOURCE_EMPTY_LABEL = 'Nevybráno';

export const ZAFUK_SOURCE_REQUIRED_MESSAGE =
  'Vyberte zdroj záfuku (rozvaděč nebo spojkoviště) z mapy.';
export const ZAFUK_SOURCE_TYPE_INVALID_MESSAGE =
  'Zdrojem záfuku může být pouze rozvaděč nebo spojkoviště.';
export const ZAFUK_SOURCE_SELF_MESSAGE = 'Zdroj záfuku nesmí být upravovaný objekt.';
export const ZAFUK_SOURCE_POLYGON_MESSAGE =
  'Zdroj záfuku musí ležet ve stejném polygonu jako cílový objekt.';
export const ZAFUK_SOURCE_NOT_FOUND_MESSAGE = 'Vybraný zdroj záfuku neexistuje.';
export const ZAFUK_SOURCE_LIST_NO_POLYGON_MESSAGE =
  'Objekt nemá přiřazený polygon — nelze načíst rozvaděče.';
export const ZAFUK_SOURCE_LIST_LOAD_ERROR = 'Nepodařilo se načíst rozvaděče v polygonu.';
export const ZAFUK_SOURCE_LIST_EMPTY_MESSAGE = 'V polygonu nejsou žádné rozvaděče.';
export const ZAFUK_SOURCE_LIST_NO_MATCH_MESSAGE = 'Žádný rozvaděč neodpovídá vyhledávání.';

// ---------------------------------------------------------------------------
// Parsování / normalizace
// ---------------------------------------------------------------------------

export function isZafukSourceObjectType(value: unknown): value is ZafukSourceObjectType {
  if (typeof value !== 'string') return false;
  const upper = value.trim().toUpperCase();
  return (ZAFUK_SOURCE_OBJECT_TYPES as readonly string[]).includes(upper);
}

/** Fail-closed: neznámý typ není zdroj (nehádá se ROZVADEC). */
export function parseZafukSourceObjectType(value: unknown): ZafukSourceObjectType | null {
  if (typeof value !== 'string') return null;
  const upper = value.trim().toUpperCase();
  return isZafukSourceObjectType(upper) ? (upper as ZafukSourceObjectType) : null;
}

function trimmedOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text || null;
}

export function zafukSourceLabel(ref: ZafukSourceRef | null | undefined): string {
  if (!ref) return ZAFUK_SOURCE_EMPTY_LABEL;
  const name = (ref.name ?? '').trim();
  const id = (ref.objectId ?? '').trim();
  const base = name && name !== id ? name : id;
  if (!base) return ZAFUK_SOURCE_EMPTY_LABEL;
  return `${base} (${ZAFUK_SOURCE_TYPE_LABELS[ref.objectType]})`;
}

export function createZafukSourceRef(
  objectId: string | null | undefined,
  objectType: unknown,
  name?: string | null,
): ZafukSourceRef | null {
  const id = trimmedOrNull(objectId);
  const type = parseZafukSourceObjectType(objectType);
  if (!id || !type) return null;
  const label = trimmedOrNull(name);
  return { objectId: id, objectType: type, name: label ?? id };
}

export function zafukSourceRefsEqual(
  a: ZafukSourceRef | null | undefined,
  b: ZafukSourceRef | null | undefined,
): boolean {
  if (!a || !b) return !a && !b;
  return a.objectId === b.objectId && a.objectType === b.objectType;
}

/**
 * Přečte zdroj z libovolného tvaru (nový neutrální, vnořený `zafukSource`, nebo legacy
 * `*Rozvadec*` klíče). Legacy klíč bez typu je vždy ROZVADEC.
 */
export function normalizeZafukSourceRef(raw: unknown): ZafukSourceRef | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;

  const nested = o['zafukSource'];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const fromNested = normalizeZafukSourceRef(nested);
    if (fromNested) return fromNested;
  }

  const neutralId =
    trimmedOrNull(o['objectId']) ??
    trimmedOrNull(o['zafukSourceObjectId']) ??
    trimmedOrNull(o['sourceObjectId']);
  const neutralType =
    parseZafukSourceObjectType(o['objectType']) ??
    parseZafukSourceObjectType(o['zafukSourceObjectType']) ??
    parseZafukSourceObjectType(o['sourceObjectType']);
  if (neutralId && neutralType) {
    const name =
      trimmedOrNull(o['name']) ??
      trimmedOrNull(o['zafukSourceName']) ??
      trimmedOrNull(o['sourceName']);
    return { objectId: neutralId, objectType: neutralType, name: name ?? neutralId };
  }

  // Legacy — vždy ROZVADEC.
  const legacyId =
    trimmedOrNull(o['sourceRozvadecObjectId']) ??
    trimmedOrNull(o['zafukZRozvadecObjectId']) ??
    trimmedOrNull(o['pripojovanyZafouknutoZRozvadecObjectId']);
  if (!legacyId) return null;
  const legacyName =
    trimmedOrNull(o['sourceRozvadecName']) ??
    trimmedOrNull(o['zafukZRozvadecName']) ??
    trimmedOrNull(o['pripojovanyZafouknutoZRozvadecName']);
  return { objectId: legacyId, objectType: 'ROZVADEC', name: legacyName ?? legacyId };
}

// ---------------------------------------------------------------------------
// Serializace
// ---------------------------------------------------------------------------

/** Neutrální ploché klíče pro DTO / sloupce. */
export function zafukSourceToFlatFields(
  ref: ZafukSourceRef | null | undefined,
): ZafukSourceFlatFields {
  if (!ref) {
    return { zafukSourceObjectId: null, zafukSourceObjectType: null, zafukSourceName: null };
  }
  return {
    zafukSourceObjectId: ref.objectId,
    zafukSourceObjectType: ref.objectType,
    zafukSourceName: (ref.name ?? '').trim() || ref.objectId,
  };
}

/**
 * Legacy zrcadlo — **jen** pro ROZVADEC. SPOJKOVISTE zdroj zůstává prázdný, aby
 * rozvaděčové dotazy (svary, linked budovy, finance) spojkoviště nepovažovaly za rozvaděč.
 */
export function zafukSourceToLegacyFields(
  ref: ZafukSourceRef | null | undefined,
): ZafukSourceLegacyFields {
  if (!ref || ref.objectType !== 'ROZVADEC') {
    return { zafukZRozvadecObjectId: null, zafukZRozvadecName: null };
  }
  return {
    zafukZRozvadecObjectId: ref.objectId,
    zafukZRozvadecName: (ref.name ?? '').trim() || ref.objectId,
  };
}

/** True, když zdroj smí zapsat legacy `*Rozvadec*` sloupce / klíče. */
export function zafukSourceWritesLegacyRozvadecFields(
  ref: ZafukSourceRef | null | undefined,
): boolean {
  return ref?.objectType === 'ROZVADEC';
}

// ---------------------------------------------------------------------------
// Validace (sdílená klient ⇄ server)
// ---------------------------------------------------------------------------

/** Cílový objekt záfuku — kontext pro validaci zdroje. */
export interface ZafukSourceTargetContext {
  /** Id upravovaného objektu (cíl záfuku). Zabraňuje self-referenci. */
  targetObjectId: string | null | undefined;
  /** Polygon cíle. Zdroj musí ležet ve stejném polygonu. */
  targetPolygonId: string | null | undefined;
}

/** Kandidát na zdroj, jak ho zná mapa / DB. */
export interface ZafukSourceCandidate {
  id: string | null | undefined;
  type: string | null | undefined;
  name?: string | null;
  polygonId?: string | null;
}

export type ZafukSourceRejectionReason =
  | 'missing'
  | 'invalid-type'
  | 'self-reference'
  | 'different-polygon';

export const ZAFUK_SOURCE_REJECTION_MESSAGES: Record<ZafukSourceRejectionReason, string> = {
  'missing': ZAFUK_SOURCE_REQUIRED_MESSAGE,
  'invalid-type': ZAFUK_SOURCE_TYPE_INVALID_MESSAGE,
  'self-reference': ZAFUK_SOURCE_SELF_MESSAGE,
  'different-polygon': ZAFUK_SOURCE_POLYGON_MESSAGE,
};

export interface ZafukSourceEligibility {
  eligible: boolean;
  /** Vyplněno jen při `eligible === true`. */
  ref: ZafukSourceRef | null;
  /** Vyplněno jen při `eligible === false`. */
  reason: ZafukSourceRejectionReason | null;
  /** Vyplněno jen při `eligible === false`. */
  message: string | null;
}

function reject(reason: ZafukSourceRejectionReason): ZafukSourceEligibility {
  return {
    eligible: false,
    ref: null,
    reason,
    message: ZAFUK_SOURCE_REJECTION_MESSAGES[reason],
  };
}

/**
 * Jediné místo pravdy pro „smí být tento objekt zdrojem záfuku?“.
 * Web, Android, iOS i backend musí používat tuto funkci (resp. její KMP port).
 *
 * Polygon se kontroluje jen když je znám u obou stran — rozpracované polygony
 * bez `polygonId` se nezamítají (parita s dosavadním chováním pickeru).
 */
export function evaluateZafukSourceCandidate(
  candidate: ZafukSourceCandidate | null | undefined,
  target: ZafukSourceTargetContext,
): ZafukSourceEligibility {
  const id = trimmedOrNull(candidate?.id);
  if (!id) return reject('missing');

  const type = parseZafukSourceObjectType(candidate?.type);
  if (!type) return reject('invalid-type');

  const targetId = trimmedOrNull(target.targetObjectId);
  if (targetId && targetId === id) return reject('self-reference');

  const targetPolygon = trimmedOrNull(target.targetPolygonId);
  const sourcePolygon = trimmedOrNull(candidate?.polygonId);
  if (targetPolygon && sourcePolygon && targetPolygon !== sourcePolygon) {
    return reject('different-polygon');
  }

  const name = trimmedOrNull(candidate?.name);
  return {
    eligible: true,
    ref: { objectId: id, objectType: type, name: name ?? id },
    reason: null,
    message: null,
  };
}

export function isZafukSourceCandidateEligible(
  candidate: ZafukSourceCandidate | null | undefined,
  target: ZafukSourceTargetContext,
): boolean {
  return evaluateZafukSourceCandidate(candidate, target).eligible;
}

/** Lidsky čitelný důvod zamítnutí, nebo `null` když je kandidát v pořádku. */
export function describeZafukSourceCandidateFailure(
  candidate: ZafukSourceCandidate | null | undefined,
  target: ZafukSourceTargetContext,
): string | null {
  const outcome = evaluateZafukSourceCandidate(candidate, target);
  if (outcome.eligible) return null;
  return outcome.message;
}

/** Typy objektů, které mapový picker během režimu výběru zvýrazní a nechá kliknout. */
export function zafukSourcePickableObjectTypes(): readonly ZafukSourceObjectType[] {
  return ZAFUK_SOURCE_OBJECT_TYPES;
}

/** True, když je typ objektu na mapě klikatelný jako zdroj záfuku. */
export function isZafukSourcePickableObjectType(type: string | null | undefined): boolean {
  return parseZafukSourceObjectType(type) != null;
}
